import { ConstructorGrid } from '@theme/constructor-grid';
import {
  query,
  buildRankedCardsURL,
  orderByRank,
  discoverSearchSectionId,
  currentSequence,
  getConfig,
  extractResultId,
} from '@theme/constructor-client';
import { StandardEvents } from '@shopify/events';

const CART_SECTION_ID_KEY = 'constructor:cart-rec-section-id';
const CART_SECTION_KEY = 'cart-recs';

/** @returns {string | null} */
function getCachedCartSectionId() {
  try {
    return sessionStorage.getItem(CART_SECTION_ID_KEY);
  } catch {
    return null;
  }
}

/** @param {string} id */
function setCachedCartSectionId(id) {
  try {
    sessionStorage.setItem(CART_SECTION_ID_KEY, id);
  } catch {
    /* private mode — every load just re-derives it */
  }
}

async function resolveCartSectionId(ids) {
  const cached = getCachedCartSectionId();
  if (cached) return cached;

  const response = await fetch(buildRankedCardsURL(ids, null));
  if (!response.ok) return null;
  const html = await response.text();
  const mainId = discoverSearchSectionId(new DOMParser().parseFromString(html, 'text/html'));
  if (!mainId?.endsWith('__main')) return null;

  const cartSectionId = mainId.replace(/__main$/, `__${CART_SECTION_KEY}`);
  setCachedCartSectionId(cartSectionId);
  return cartSectionId;
}

/**
 * "Complete your stack" — cart-drawer recommendations ranked against every
 * product currently in the cart, not a single product like the PDP's own
 * constructor-recs-component (assets/constructor-recs.js).
 *
 * Re-requests on every cart change (add/remove/qty), since the ranking input
 * — the cart's own product ids — changes with it. The PDP version never
 * needs this: the product it ranks against is fixed for the page's lifetime.
 *
 * @extends {ConstructorGrid}
 */
class ConstructorCartRecs extends ConstructorGrid {
  /** Waits for the drawer to be open before reporting the pod as viewed. */
  #viewObserver = new IntersectionObserver((entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    this.#viewObserver.disconnect();
    this.#pendingView?.();
    this.#pendingView = undefined;
  });

  /** @type {(() => void) | undefined} */
  #pendingView;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    this.#viewObserver.disconnect();
    this.#pendingView = undefined;
  }

  /** @returns {import('@theme/constructor-tracker').ResultContext} */
  trackingContext() {
    return {
      ...super.trackingContext(),
      podId: this.dataset.podId,
      seedItemIds: this.dataset.itemIds ? this.dataset.itemIds.split(',') : undefined,
      url: location.href,
    };
  }

  /**
   * The pod renders inside the cart drawer whether or not the drawer is open, and
   * re-renders on every cart change. Only a render the shopper could see counts
   * as viewed; a newer render replaces one still waiting.
   * @param {Element[]} cards
   */
  reportResults(cards) {
    this.#pendingView = () => super.reportResults(cards);
    this.#viewObserver.disconnect();
    this.#viewObserver.observe(this);
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        const ids = (cart?.items ?? []).map((item) => item.product_id).filter(Boolean);
        this.dataset.itemIds = [...new Set(ids)].join(',');
        this.load();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[constructor-cart-recs] Event promise rejected:', error);
      });
  };

  /**
   * An empty cart has no items to rank against, but that's not the same as having nothing to
   * show: the configured pod (e.g. a best-sellers strategy) can still return results with no
   * item context at all, the same way it would for a first-time visitor with an empty cart on
   * any other surface. itemIds is only included when the cart actually has items.
   * @returns {Promise<(string | number)[]>}
   */
  async requestIds() {
    const podId = this.dataset.podId;
    if (!podId) return [];
    const itemIds = (this.dataset.itemIds || '').split(',').filter(Boolean);

    const { data } = await query(this.surface, (client, signal) =>
      client.recommendations.getRecommendations(
        podId,
        {
          ...(itemIds.length ? { itemIds } : {}),
          numResults: Number(this.dataset.podLimit) || 10,
        },
        { signal }
      )
    );

    /** @type {{ data?: { id?: string } }[]} */
    const results = data?.response?.results ?? [];
    this.dataset.strategyId = data?.response?.pod?.id || '';
    this.resultId = extractResultId(data);
    this.totalResults = data?.response?.total_num_results ?? results.length;

    return results
      .map((result) => result?.data?.id)
      .filter(/** @returns {id is string} */ (id) => id != null);
  }

  async renderRanked(ids) {
    const seq = currentSequence(this.surface);
    const wanted = ids.slice(0, 48);

    const sectionId = await resolveCartSectionId(wanted);
    if (currentSequence(this.surface) !== seq) return;
    if (!sectionId) {
      this.handleFailure(new Error('cart-recs: could not resolve section id'));
      return;
    }

    const url = buildRankedCardsURL(wanted, sectionId);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), getConfig()?.timeoutMs ?? 8000);

    let html;
    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) {
        throw Object.assign(new Error(`cards fetch ${response.status}`), { status: response.status });
      }
      html = await response.text();
    } catch (error) {
      if (currentSequence(this.surface) === seq) this.handleFailure(error);
      return;
    } finally {
      clearTimeout(timer);
    }

    if (currentSequence(this.surface) !== seq) return;

    const cards = orderByRank(
      this.extractCards(new DOMParser().parseFromString(html, 'text/html')),
      wanted
    );

    if (!cards.length) {
      this.showEmpty();
      return;
    }

    for (const card of cards) {
      card.setAttribute('data-cnstrc-item', this.itemContext);
    }

    if (!this.placeCards(cards)) {
      this.handleFailure(new Error('no placement target'));
      return;
    }

    this.setAttribute('data-cnstrc-num-results', String(cards.length));
    this.renderedCount = cards.length;
    this.hasRenderedSuccessfully = true;
    this.reportResults(cards);
    delete this.dataset.constructorEmpty;
    delete this.dataset.constructorFallback;
    this.dataset.constructorLoaded = 'true';

    this.dispatchEvent(
      new CustomEvent('constructor:rendered', {
        detail: { surface: this.surface, count: cards.length },
        bubbles: true,
      })
    );
  }
}

if (!customElements.get('constructor-cart-recs-component')) {
  customElements.define('constructor-cart-recs-component', ConstructorCartRecs);
}
